import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, inject, input, output, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { faDiagramCells, faFileExport, faGripDotsVertical, faLaptopBinary } from '@fortawesome/pro-regular-svg-icons';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { Comment } from '@empathy/comments/data';
import { CommentsListComponent } from '@empathy/comments/feature';
import { PreviewDevice } from '@empathy/survey/base';

import { MainTabs } from './../../models/main-tabs.enum';
import { GeneralProgrammerNotesDialogComponent } from '../../components/general-programmer-notes-dialog/general-programmer-notes-dialog.component';
import { QuestionTypeSelectComponent } from '../../components/question-type-select/question-type-select.component';
import { NotepadQuestionForm } from '../../models/notepad-form.model';
import { NotepadQuestionTypeConfig } from '../../models/notepad-question-type-config.constant';
import { ExtendedQuestionType } from '../../models/notepad-question-type.constant';
import { NotepadTab } from '../../models/notepad-tabs.enum';
import { Notepad, NotepadIdPrefix } from '../../models/notepad.model';
import { NotepadService } from '../../services/notepad.service';

@Component({
  selector: 'empathy-notepad',
  templateUrl: './notepad.component.html',
  styleUrl: './notepad.component.scss',
  animations: [
    trigger('cardAnimation', [
      state('void', style({ opacity: 0, transform: 'translateY(-2%)' })),
      state('*', style({ opacity: 1 })),
      transition(':enter', [animate('250ms cubic-bezier(.17,.67,1,1.04)', style({ opacity: 1, transform: 'translateY(0)' }))]),
      transition(':leave', [animate('250ms cubic-bezier(.17,.67,1,1.04)', style({ opacity: 0, transform: 'translateX(100%)', height: 0 }))])
    ]),
    trigger('enterAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('100ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('100ms', style({ opacity: 0 }))])
    ])
  ]
})
export class NotepadComponent implements AfterViewInit, OnDestroy {
  @ViewChild(QuestionTypeSelectComponent) typeComponent!: QuestionTypeSelectComponent;
  @ViewChild(CommentsListComponent) commentList!: CommentsListComponent;
  @ViewChild('header') notepadHeader: ElementRef;

  notepad = input.required<Notepad>();
  notepadComments = input<Comment[]>();
  readonly = input(false);
  canComment = input(false);

  addComment = output<string>();
  export = output<string>();
  fullPreview = output<void>();
  postReply = output<Comment>();
  updateComment = output<Comment>();
  save = output<void>();

  viewportHeaderObserver$: IntersectionObserver;

  commentsCount$ = signal([]);
  fullPreview$ = signal(false);
  saving$ = signal(false);
  showAddQuestion$ = signal(false);
  showFloatingHeader$ = signal(false);
  activeOverlayOrigin$ = signal<CdkOverlayOrigin>(null);
  selectedTab$ = signal(NotepadTab.Comments);
  tab$ = signal(MainTabs.NotepadEditor);

  faDiagramCells = faDiagramCells;
  faFileExport = faFileExport;
  faGripDotsVertical = faGripDotsVertical;
  faLaptopBinary = faLaptopBinary;

  previewDevice = PreviewDevice;

  dialog = inject(MatDialog);

  private notepadService = inject(NotepadService);

  private destroyed$ = new Subject<void>();

  ngAfterViewInit(): void {
    this.notepad()
      .form.valueChanges.pipe(takeUntil(this.destroyed$), debounceTime(1000))
      .subscribe(() => {
        this.save.emit();
      });

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.9
    };

    this.viewportHeaderObserver$ = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.showFloatingHeader$.set(false);
        } else {
          this.showFloatingHeader$.set(true);
        }
      });
    }, options);

    this.viewportHeaderObserver$.observe(this.notepadHeader.nativeElement);
  }

  ngOnDestroy(): void {
    this.viewportHeaderObserver$.unobserve(this.notepadHeader.nativeElement);
    this.viewportHeaderObserver$.disconnect();

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getQuestions(): FormArray<FormGroup<NotepadQuestionForm>> {
    return this.notepad().form.get('questions') as FormArray<FormGroup<NotepadQuestionForm>>;
  }

  onToggleAddQuestion(origin: CdkOverlayOrigin): void {
    this.activeOverlayOrigin$.set(origin);
    this.showAddQuestion$.set(true);
  }

  onAddQuestion(type: ExtendedQuestionType): void {
    this.showAddQuestion$.set(false);
    const questionId = `${NotepadIdPrefix}${this.notepad().form.get('questions')?.value.length + 1}`;
    const questionsForm = this.notepad().form.get('questions') as FormArray;
    const newQuestion = NotepadQuestionTypeConfig[type].factory(questionId, type);
    const newQuestionForm = this.notepadService.getQuestionForm(newQuestion);

    questionsForm.push(newQuestionForm);

    requestAnimationFrame(() => {
      document.getElementById(`question-${questionId}`).scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  onAddComment(questionId: string): void {
    this.commentList.addNewComment(questionId);
  }

  onFilterComments(questionId: string): void {
    this.commentList.onFilterComments(`#${questionId}`);
  }

  onParseCommentsCount(parsedHashtags: { hashtag: string; count: number }[]): void {
    this.commentsCount$.set(parsedHashtags);
  }

  onCommentsCountById(id: string): number {
    return this.commentsCount$().find((c) => c.hashtag.toLocaleLowerCase() === `#${id.toLocaleLowerCase()}`)?.count ?? 0;
  }

  onTabChange(index: number): void {
    this.selectedTab$.set(index);
  }

  onScrollToComment(id: string): void {
    if (id !== '') {
      const questionId = id.replace('#', '').toUpperCase();

      const questionNumber = questionId.split(NotepadIdPrefix).filter((char) => char);
      const hasOneQ = questionNumber.length === 1;
      const hasOnlyNumbersExceptQ = questionNumber.every((char) => !isNaN(Number(char)));

      if (hasOneQ && hasOnlyNumbersExceptQ) {
        if (Number(questionNumber) <= this.getQuestions().controls.length) {
          requestAnimationFrame(() => {
            document.getElementById(`question-${questionId}`).scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      }
    }
  }

  onUpdateNotepadQuestions(index: number, question: FormGroup): void {
    const questions = this.notepad().form.get('questions') as FormArray;
    questions.setControl(index, question);
  }

  onDeleteNotepadQuestion(index: number): void {
    const questions = this.notepad().form.get('questions') as FormArray;

    questions.removeAt(index);
  }

  onReorder(event: CdkDragDrop<FormControl[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      const questions = this.getQuestions().controls;
      moveItemInArray(questions, event.previousIndex, event.currentIndex);
      (this.notepad().form.get('questions') as FormArray).patchValue(questions);
    }
  }

  onOpenProgrammerNotes(): void {
    this.dialog
      .open(GeneralProgrammerNotesDialogComponent, { data: { programmerNotes: this.notepad().form.get('programmerNotes').value } })
      .afterClosed()
      .subscribe((savedNotes) => {
        this.notepad().form.get('programmerNotes').setValue(savedNotes);
      });
  }
}
